import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from "react";
import toast from "react-hot-toast";
import { useAuth } from "./AuthContext";
import { authGet, authPost, authDelete } from "../utils/authFetch";

const FavoritesContext = createContext();

const API_URL = import.meta.env.VITE_API_URL;

// Helper to get ID from business object
const getId = (item) => item.id || item._id;

export const FavoritesProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const syncedRef = useRef(false);

  // ── Read from localStorage (fallback) ──
  const loadFromLocalStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem("favorites");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const saveToLocalStorage = useCallback((items) => {
    localStorage.setItem("favorites", JSON.stringify(items));
  }, []);

  // ── Fetch favorites from backend ──
  const fetchFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavorites(loadFromLocalStorage());
      return;
    }

    setLoading(true);
    try {
      const res = await authGet(`${API_URL}/api/favorites`);
      const data = await res.json();
      if (data.success) {
        // Map backend response to business objects
        const mapped = (data.data || []).map((fav) => {
          // If the backend returns { business: {...} }, extract it
          if (fav.business && typeof fav.business === "object") {
            return { ...fav.business, _favoriteId: fav._id };
          }
          return fav;
        });
        setFavorites(mapped);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
      // Fallback to localStorage on error
      setFavorites(loadFromLocalStorage());
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loadFromLocalStorage]);

  // ── Sync localStorage favorites to backend on first auth ──
  const syncLocalToBackend = useCallback(async () => {
    const localFavs = loadFromLocalStorage();
    if (localFavs.length === 0) return;

    try {
      for (const biz of localFavs) {
        const bizId = getId(biz);
        if (bizId) {
          await authPost(`${API_URL}/api/favorites`, { businessId: bizId });
        }
      }
      // Clear localStorage after successful sync
      localStorage.removeItem("favorites");
    } catch (error) {
      console.error("Error syncing favorites to backend:", error);
    }
  }, [loadFromLocalStorage]);

  // ── Load favorites when auth state changes ──
  useEffect(() => {
    if (isAuthenticated && user?._id) {
      // Sync local favorites to backend once per session
      if (!syncedRef.current) {
        syncedRef.current = true;
        syncLocalToBackend().then(() => fetchFavorites());
      } else {
        fetchFavorites();
      }
    } else {
      syncedRef.current = false;
      setFavorites(loadFromLocalStorage());
    }
  }, [isAuthenticated, user?._id, fetchFavorites, syncLocalToBackend, loadFromLocalStorage]);

  // ── Toggle favorite ──
  const toggleFavorite = useCallback(async (business) => {
    const businessId = getId(business);
    const wasAlreadyFavorite = favorites.some((f) => getId(f) === businessId);

    if (isAuthenticated) {
      // Optimistic update
      if (wasAlreadyFavorite) {
        setFavorites((prev) => prev.filter((f) => getId(f) !== businessId));
        toast.error("Eliminado de favoritos", { id: 'favorite-toast' });
        try {
          await authDelete(`${API_URL}/api/favorites/${businessId}`);
        } catch (error) {
          console.error("Error removing favorite:", error);
          // Revert on error
          setFavorites((prev) => [...prev, business]);
        }
      } else {
        setFavorites((prev) => [...prev, business]);
        toast.success("Agregado a favoritos", { id: 'favorite-toast' });
        try {
          await authPost(`${API_URL}/api/favorites`, { businessId });
        } catch (error) {
          console.error("Error adding favorite:", error);
          // Revert on error
          setFavorites((prev) => prev.filter((f) => getId(f) !== businessId));
        }
      }
    } else {
      // localStorage fallback
      if (wasAlreadyFavorite) {
        const updated = favorites.filter((f) => getId(f) !== businessId);
        setFavorites(updated);
        saveToLocalStorage(updated);
        toast.error("Eliminado de favoritos", { id: 'favorite-toast' });
      } else {
        const updated = [...favorites, business];
        setFavorites(updated);
        saveToLocalStorage(updated);
        toast.success("Agregado a favoritos", { id: 'favorite-toast' });
      }
    }
  }, [favorites, isAuthenticated, saveToLocalStorage]);

  // ── Remove favorite ──
  const removeFavorite = useCallback(async (id) => {
    setFavorites((prev) => prev.filter((f) => getId(f) !== id));
    toast.error("Eliminado de favoritos", { id: 'favorite-toast' });

    if (isAuthenticated) {
      try {
        await authDelete(`${API_URL}/api/favorites/${id}`);
      } catch (error) {
        console.error("Error removing favorite:", error);
      }
    } else {
      const updated = favorites.filter((f) => getId(f) !== id);
      saveToLocalStorage(updated);
    }
  }, [favorites, isAuthenticated, saveToLocalStorage]);

  // ── Check if favorite ──
  const isFavorite = useCallback((id) => {
    return favorites.some(
      (f) => getId(f) === id || getId(f) === String(id)
    );
  }, [favorites]);

  // ── Clear all ──
  const clearFavorites = useCallback(() => {
    setFavorites([]);
    if (!isAuthenticated) {
      localStorage.removeItem("favorites");
    }
  }, [isAuthenticated]);

  const contextValue = useMemo(() => ({
    favorites, loading, toggleFavorite, removeFavorite, isFavorite, clearFavorites
  }), [favorites, loading, toggleFavorite, removeFavorite, isFavorite, clearFavorites]);

  return (
    <FavoritesContext.Provider value={contextValue}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
};

export default FavoritesContext;
